import type { ViewerProvider, ViewerLaunchParams, ViewerConfig, ViewerSyncAdapter } from '../types';
import { FULL_SYNC_CAPABILITIES } from '../types';
import type { MonitorChannel } from '../../multi-monitor/MonitorChannel';
import { OHIFSyncAdapter } from '../../multi-monitor/OHIFSyncAdapter';

/**
 * OHIF v3.12 viewer provider — iframe-based (source build on NodePort 30082).
 *
 * POC: Demonstrates multi-viewer support by running two OHIF versions
 * side by side. In production, this would be replaced by a single OHIF
 * provider once the v3.12 migration is complete.
 *
 * Uses a direct NodePort URL for now — in production the ingress
 * would route /ohif-v312/ to the v3.12 service.
 */
export const OHIF312ViewerProvider: ViewerProvider = {
  id: 'ohif-v312',
  name: 'OHIF v3.12 (Source Build)',
  supportsHangingProtocols: false, // Not wired yet — Phase 2+
  supportsComparisons: false,
  syncCapabilities: FULL_SYNC_CAPABILITIES, // Same postMessage bridge as v3.9

  buildViewerConfig({ study, siteId, sessionId }: ViewerLaunchParams): ViewerConfig {
    // v3.12 vanilla build — use default longitudinal mode route
    const params = new URLSearchParams({
      StudyInstanceUIDs: study.uid,
      siteId,
      session: sessionId,
    });

    // Same-origin relative URL — routes through brit-web's nginx proxy
    // to the ohif-viewer-v312 k8s service, just like v3.9.2 uses /ohif/
    return {
      type: 'iframe' as const,
      url: `/ohif3/viewer?${params.toString()}`,
    };
  },

  createSyncAdapter(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ): ViewerSyncAdapter {
    return new OHIFSyncAdapter(channel, windowId, iframeRef);
  },
};
